import React, { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Box, IconButton, CircularProgress, useTheme, useMediaQuery, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export default function PdfViewerPanel({
    userlevelDepartment,
    file,
    onClose,
    initialWidth = 400,
    minWidth = 0,
    maxWidth = 800,
}) {
    const theme = useTheme();
    const isMobile = true; // Force mobile-like behavior (bottom drawer) always

    const [numPages, setNumPages] = useState(null);

    const [containerWidth, setContainerWidth] = useState(initialWidth);
    const [currentWidth, setCurrentWidth] = useState(initialWidth);
    const [panelHeight, setPanelHeight] = useState(() => window.innerHeight * 0.8);
    const [isDragging, setIsDragging] = useState(false);

    const dragWidthRef = useRef(initialWidth);
    const startXRef = useRef(0);
    const startDragWidthRef = useRef(initialWidth);

    const dragHeightRef = useRef(0);
    const startYRef = useRef(0);
    const [pdfWidth, setPdfWidth] = useState(window.innerWidth);

    const [zoom, setZoom] = useState(1.0);

    const pdfRef = useRef();

    useEffect(() => {
        const handleResize = () => {
            setPdfWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Pinch zoom for mobile
    useEffect(() => {
        const elem = pdfRef.current;
        if (!elem) return;

        let initialDistance = 0;

        const getDistance = (t1, t2) => Math.sqrt((t1.clientX - t2.clientX) ** 2 + (t1.clientY - t2.clientY) ** 2);

        const handleTouchStart = (e) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                initialDistance = getDistance(e.touches[0], e.touches[1]);
            }
        };

        const handleTouchMove = (e) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                const newDistance = getDistance(e.touches[0], e.touches[1]);
                if (initialDistance > 0) {
                    const ratio = newDistance / initialDistance;
                    setZoom((prev) => Math.max(0.5, Math.min(3, prev * ratio)));
                    initialDistance = newDistance;
                }
            }
        };

        elem.addEventListener('touchstart', handleTouchStart, { passive: false });
        elem.addEventListener('touchmove', handleTouchMove, { passive: false });

        return () => {
            elem.removeEventListener('touchstart', handleTouchStart);
            elem.removeEventListener('touchmove', handleTouchMove);
        };
    }, []);

    // --- MOBILE: Vertical drag (mouse + touch) ---
    const onDragVerticalMove = (clientY) => {
        setIsDragging(true);
        const delta = dragHeightRef.current - (clientY - startYRef.current);
        const newHeight = Math.min(Math.max(100, delta), window.innerHeight * 0.9);
        setPanelHeight(newHeight);
    };

    const onMouseMoveVertical = (e) => {
        e.preventDefault();
        onDragVerticalMove(e.clientY);
    };

    const onMouseUpVertical = () => {
        setIsDragging(false);
        window.removeEventListener('mousemove', onMouseMoveVertical);
        window.removeEventListener('mouseup', onMouseUpVertical);
    };

    const onMouseDownVertical = (e) => {
        e.preventDefault();
        startYRef.current = e.clientY;
        dragHeightRef.current = panelHeight;
        window.addEventListener('mousemove', onMouseMoveVertical);
        window.addEventListener('mouseup', onMouseUpVertical);
    };

    const onTouchStartVertical = (e) => {
        startYRef.current = e.touches[0].clientY;
        dragHeightRef.current = panelHeight;
        window.addEventListener('touchmove', onTouchMoveVertical, { passive: false });
        window.addEventListener('touchend', onTouchEndVertical);
    };

    const onTouchMoveVertical = (e) => {
        e.preventDefault();
        onDragVerticalMove(e.touches[0].clientY);
    };

    const onTouchEndVertical = () => {
        setIsDragging(false);
        window.removeEventListener('touchmove', onTouchMoveVertical);
        window.removeEventListener('touchend', onTouchEndVertical);
    };

    useEffect(() => {
        const cleanup = () => {
            setIsDragging(false);
        };
        window.addEventListener('mouseup', cleanup);
        window.addEventListener('touchend', cleanup);
        return () => {
            window.removeEventListener('mouseup', cleanup);
            window.removeEventListener('touchend', cleanup);
        };
    }, []);

    return (
        <Box
            sx={{
                height: `${panelHeight}px`,
                width: '100%',
                position: 'fixed',
                bottom: 0,
                right: 'auto',
                left: 0,
                top: 'auto',
                borderLeft: 'none',
                borderTop: '1px solid #ccc',
                backgroundColor: '#fff',
                display: 'flex',
                flexDirection: 'column',
                zIndex: 2000,
                overflow: 'hidden',
                maxWidth: '100%',
            }}
        >


            {/* Mobile: draggable top bar */}
            <Box
                onMouseDown={onMouseDownVertical}
                onTouchStart={onTouchStartVertical}
                sx={{
                    height: 20,
                    cursor: 'row-resize',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#ddd',
                }}
            >
                <DragHandleIcon fontSize="small" sx={{ opacity: 0.6 }} />
            </Box>

            {/* Title */}
            {/* <Typography textAlign="center" width={"100%"}>{userlevelDepartment == "leader" ? "Dokumentationsleitpfaden für Leiter" : "Dokumentationsleitpfaden für Benutzer"}</Typography> */}

            {/* Top bar with zoom (desktop) and close */}
            <Box sx={{ p: 1, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <IconButton onClick={() => setZoom((prev) => Math.max(0.5, prev - 0.25))}>
                        <ZoomOutIcon />
                    </IconButton>
                    <IconButton onClick={() => setZoom((prev) => Math.min(3, prev + 0.25))}>
                        <ZoomInIcon />
                    </IconButton>
                </Box>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* PDF */}
            <Box ref={pdfRef} sx={{ flexGrow: 1, overflow: 'auto', px: 0, position: 'relative' }}>
                {isDragging && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            backgroundColor: 'rgba(255,255,255,0.7)',
                            zIndex: 1000,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}

                {!isDragging && (
                    <Document
                        file={file}
                        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                        loading={
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '100%',
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        }
                    >
                        {Array.from(new Array(numPages), (_, index) => (
                            <Box
                                key={`page_${index + 1}`}
                                sx={{
                                    mb: 4,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    '& > canvas': {
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        border: '1px solid #ddd',
                                        backgroundColor: '#fff',
                                        borderRadius: 2,
                                    },
                                }}>
                                <Page
                                    pageNumber={index + 1}
                                    renderMode="canvas"
                                    width={pdfWidth * zoom} />
                            </Box>
                        ))}
                    </Document>
                )}
            </Box>

        </Box>
    );
}